import React, { useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import BaseApi from '../../../api/BaseApi.js';
import ScreenCaption from '../../screen-caption.js';

export default function EventsScreen({ navigate, goback, args: initialArgs }) {
  // --- Инициализация аргументов и начальных фильтров (как было в constructor) ---
  const initial = useMemo(() => {
    let instituteId = null;
    let selectedInstituteId = "";
    let programId = null;
    let selectedProgramId = "";
    let projectId = null;
    let selectedProjectId = "";
    let typeId = null;
    let selectedTypeId = "";
    let status = null;
    let selectedStatus = "";
    let beneficiaryid = "";
    let pageCount = 0;
    let pageLimit = 25;

    const args = initialArgs || {};

    if (args.selectedInstituteId) {
      selectedInstituteId = args.selectedInstituteId;
    }

    if (args.instituteid !== undefined) {
      instituteId = args.instituteid;
      selectedInstituteId = instituteId;
    }

    if (args.selectedProgramId) {
      selectedProgramId = args.selectedProgramId;
    }

    if (args.programid !== undefined) {
      programId = args.programid;
      selectedProgramId = programId;
    }

    if (args.selectedProjectId) {
      selectedProjectId = args.selectedProjectId;
    }

    if (args.projectid !== undefined) {
      projectId = args.projectid;
      selectedProjectId = projectId;
    }

    if (args.typeid !== undefined) {
      typeId = args.typeid;
      selectedTypeId = typeId;
    }

    if (args.status !== undefined) {
      status = args.status;
      selectedStatus = status;
    }

    if (args.beneficiary !== undefined) {
      beneficiaryid = args.beneficiary;
    }

    if (args.pageCount !== undefined) {
      pageCount = args.pageCount;
    }

    return {
      args,
      instituteId,
      selectedInstituteId,
      programId,
      selectedProgramId,
      projectId,
      selectedProjectId,
      typeId,
      selectedTypeId,
      status,
      selectedStatus,
      beneficiaryid,
      pageCount,
      pageLimit,
    };
  }, [initialArgs]);

  // --- Состояния ---
  const [args, setArgs] = useState(initial.args);

  const [instituteOptions, setInstituteOptions] = useState([]);
  const [programOptions, setProgramOptions] = useState([]);
  const [typeOptions, setTypeOptions] = useState([]);
  const [projectOptions, setProjectOptions] = useState([]);
  const [statusOptions] = useState([
    { value: 'planned', label: 'Запланировано' },
    { value: 'completed', label: 'Завершено' },
    { value: 'canceled', label: 'Отменено' },
    { value: '', label: 'Все'}
  ]);

  // фиксированные фильтры из args (instituteId / programId задаются при входе и не меняются)
  const [instituteId] = useState(initial.instituteId);
  const [programId] = useState(initial.programId);
  const [projectId] = useState(initial.projectId);
  const [typeId] = useState(initial.typeId);
  const [beneficiaryid] = useState(initial.beneficiaryid);
  const [pageCount, setPageCount] = useState(initial.pageCount);
  const [pageLimit] = useState(initial.pageLimit);

  // текущие выбранные значения фильтров
  const [selectedInstituteId, setSelectedInstituteId] = useState(initial.selectedInstituteId);
  const [selectedProgramId, setSelectedProgramId] = useState(initial.selectedProgramId);
  const [selectedTypeId, setSelectedTypeId] = useState(initial.selectedTypeId);
  const [selectedProjectId, setSelectedProjectId] = useState(initial.selectedProjectId);
  const [selectedStatus, setSelectedStatus] = useState(initial.selectedStatus);

  const [events, setEvents] = useState([]);

  // --- Выбор option-ов для Select по текущим значениям ---
  const selectedInstituteOption = useMemo(
    () => instituteOptions.find((o) => o.value === selectedInstituteId) || null,
    [instituteOptions, selectedInstituteId]
  );

  const selectedProgramOption = useMemo(
    () => programOptions.find((o) => o.value === selectedProgramId) || null,
    [programOptions, selectedProgramId]
  );

  const selectedTypeOption = useMemo(
    () => typeOptions.find((o) => o.value === selectedTypeId) || null,
    [typeOptions, selectedTypeId]
  );

  const selectedStatusOption = useMemo(
    () => statusOptions.find((o) => o.value === selectedStatus) || null,
    [statusOptions, selectedStatus]
  )

  const selectedProjectOption = useMemo(
    () => projectOptions.find((o) => o.value === selectedProjectId) || null,
    [projectOptions, selectedProjectId]
  );

  // --- Загрузка справочников ---
  useEffect(() => {
    getInstituteOptions();
    getProgramOptions();
    getTypeOptions();
    getProjectOptions();
  }, []);

  // --- Загрузка списка событий при изменении фильтров ---
  useEffect(() => {
    getDataWithCriteria();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedInstituteId, selectedProgramId, selectedProjectId, selectedTypeId, selectedStatus, beneficiaryid, pageCount]);

  // --- Handlers фильтров ---
  const institutionChangeHandler = (selectedOption) => {
    const value = selectedOption ? selectedOption.value : "";
    setSelectedInstituteId(value);
    setPageCount(0)
    setArgs((prev) => ({ ...prev, selectedInstituteId: value, pageCount: 0}));
  };

  const programChangeHandler = (selectedOption) => {
    const value = selectedOption ? selectedOption.value : "";
    setSelectedProgramId(value);
    setPageCount(0)
    setArgs((prev) => ({ ...prev, selectedProgramId: value, pageCount:0 }));
  };

  const projectChangeHandler = (selectedOption) => {
    const value = selectedOption ? selectedOption.value : "";
    setSelectedProjectId(value);
    setPageCount(0)
    setArgs((prev) => ({ ...prev, selectedProjectId: value, pageCount:0 }));
  };

  const typeChangeHandler = (selectedOption) => {
    const value = selectedOption ? selectedOption.value : "";
    setSelectedTypeId(value);
    setPageCount(0)
    setArgs((prev) => ({ ...prev, selectedTypeId: value, pageCount: 0}));
  };

  const statusChangeHandler = (selectedOption) => {
    const value = selectedOption ? selectedOption.value : "";
    setSelectedStatus(value);
    setPageCount(0)
    setArgs((prev) => ({ ...prev, selectedStatus: value, pageCount: 0}));
  }

  const prevPage = () => {
    const value = pageCount
    if (value > 0) {
      setPageCount(value-1)
      setArgs((prev) => ({...prev, pageCount: value - 1}))
    }
  };

  const nextPage = () => {
    const value = pageCount
    setPageCount(value+1)
    setArgs((prev) => ({...prev, pageCount: value + 1}))
  };

  // --- API вызовы ---
  function getInstituteOptions() {
    BaseApi.get("/institutions", {
      headers: {
        'Content-Type': 'application/json',
        'SESSIONID': sessionStorage.getItem("session"),
      },
      params: { list: true },
      withCredentials: true,
    })
      .then((res) => {
        const opts = res.data.map(({ id, name }) => ({ value: id, label: name }));
        opts.push({ value: "", label: "Все" });
        setInstituteOptions(opts);
      })
      .catch(() => {});
  }

  function getProgramOptions() {
    BaseApi.get("/programs", {
      headers: {
        'Content-Type': 'application/json',
        'SESSIONID': sessionStorage.getItem("session"),
      },
      withCredentials: true,
    })
      .then((res) => {
        const opts = res.data.map(({ id, name }) => ({ value: id, label: name }));
        opts.push({ value: "", label: "Все" });
        setProgramOptions(opts);
      })
      .catch(() => {});
  }

  function getProjectOptions() {
    BaseApi.get("/project/projects", {
      headers: {
        'Content-Type': 'application/json',
        'SESSIONID': sessionStorage.getItem("session"),
      },
      withCredentials: true,
    })
      .then((res) => {
        const opts = res.data.map(({ id, name }) => ({ value: id, label: name }));
        opts.push({ value: "", label: "Все" });
        setProjectOptions(opts);
      })
      .catch(() => {});
  }

  function getTypeOptions() {
    BaseApi.get("/event/types", {
      headers: {
        'Content-Type': 'application/json',
        'SESSIONID': sessionStorage.getItem("session"),
      },
      withCredentials: true,
    })
      .then((res) => {
        const opts = res.data.map(({ id, name }) => ({ value: id, label: name }));
        opts.push({ value: "", label: "Все" });
        setTypeOptions(opts);
      })
      .catch(() => {});
  }

  function getDataWithCriteria() {
    const parameters = {};

    if (selectedInstituteId && selectedInstituteId.length > 0) {
      parameters.instituteid = selectedInstituteId;
    }

    if (selectedProgramId && selectedProgramId.length > 0) {
      parameters.programid = selectedProgramId;
    }

    if (selectedTypeId && selectedTypeId.length > 0) {
      parameters.typeid = selectedTypeId;
    }

    if (beneficiaryid && beneficiaryid.length > 0) {
      parameters.beneficiaryid = beneficiaryid;
    }

    if (selectedProjectId && selectedProjectId.length > 0) {
      parameters.projectid = selectedProjectId
    }

    if (selectedStatus && selectedStatus.length > 0) {
      parameters.status = selectedStatus
    }

    parameters.limit = pageLimit;
    parameters.offset = pageCount * pageLimit;

    BaseApi.get('/events', {
      headers: {
        'Content-Type': 'application/json',
        'SESSIONID': sessionStorage.getItem("session"),
      },
      params: parameters,
      withCredentials: true,
    })
      .then((res) => {
        setEvents(res.data);
      })
      .catch((error) => console.log(error));
  }

  // --- Навигация в карточку события ---
  const openEvent = (id) => {
    navigate("event", { caller: "events", callargs: args, event: id });
  };

  // --- Отрисовка строки таблицы ---
  const renderEventRow = (event) => (
    <tr key={event.id} onClick={() => openEvent(event.id)} style={{ cursor: 'pointer' }}>
      {projectId == null && <td>{event.project}</td>}
      {instituteId === null && <td style={{textAlign: 'left',  padding: '4px'}}>{event.institution}</td>}
      {programId === null && <td style={{textAlign: 'left',  padding: '4px'}}>{event.program}</td>}
      <td style={{textAlign: 'left',  padding: '4px'}}>{event.product}</td>
      <td>{event.type}</td>
      <td>
        {event.status === "planned" &&  <span class="text-info"><i class="bi bi-calendar3"></i></span>} 
        {event.status === "completed" &&  <span class="text-success"><i class="bi bi-check-circle"></i></span>}
        {event.status == "canceled" && <span class="text-danger"><i class="bi bi-x-octagon"></i></span>}      
      </td>
      <td class="text-start p-1" style={{ whiteSpace: 'nowrap' }}>{event.start}</td>
      <td class="text-start p-1" style={{ whiteSpace: 'nowrap' }}>{event.end}</td>
    </tr>
  );

  // --- Рендер ---
  return (
    <div className="container">
      <ScreenCaption goback={goback} args={args} caption={"События"} />

      {/* Фильтр + кнопка "Добавить" */}
      <div className="row mt-4 mb-3">
        <div className="col-lg-5">
          <div className="card shadow-sm">
            <div className="card-body">
              <h6 className="card-title mb-3">Фильтр</h6>

              {projectId === null && (
                <div className="mb-3">
                  <label htmlFor="project" className="form-label">Проект</label>
                  <Select
                    options={projectOptions}
                    id="project"
                    onChange={projectChangeHandler}
                    value={selectedProjectOption}
                    placeholder="Все проекты"
                  />
                </div>
              )}

              {instituteId === null && (
                <div className="mb-3">
                  <label htmlFor="institution" className="form-label">Учреждение</label>
                  <Select
                    options={instituteOptions}
                    id="institution"
                    onChange={institutionChangeHandler}
                    value={selectedInstituteOption}
                    placeholder="Все учреждения"
                  />
                </div>
              )}

              {programId === null && (
                <div className="mb-3">
                  <label htmlFor="program" className="form-label">Программа</label>
                  <Select
                    options={programOptions}
                    id="program"
                    onChange={programChangeHandler}
                    value={selectedProgramOption}
                    placeholder="Все программы"
                  />
                </div>
              )}

              <div className="mb-1">
                <label htmlFor="type" className="form-label">Тип</label>
                <Select
                  options={typeOptions}
                  id="type"
                  onChange={typeChangeHandler}
                  value={selectedTypeOption}
                  placeholder="Все типы"
                />
              </div>
              <div className="mb-1">
                <label htmlFor="status" className="form-label">Статус</label>
                <Select
                  options={statusOptions}
                  id="status"
                  onChange={statusChangeHandler}
                  value={selectedStatusOption}
                  placeholder="Все статусы"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Кнопка "Добавить событие" справа */}
        <div className="col-lg-6 d-flex flex-column align-items-end mt-3 mt-lg-0">
          <div className="mb-2">
            <button
              type="button"
              className="btn btn-primary d-flex align-items-center"
              onClick={() => openEvent("")}
            >
              {/*<img src={add} height="16" width="16" className="me-2" alt="Добавить" />*/}
              Добавить событие
            </button>
          </div>

          <div className="mb-2">
            <button
                type="button"
                className="btn btn-outline-secondary btn-sm me-2"
                onClick={() => prevPage()}
                disabled={pageCount === 0}
            >
                Предыдущие
            </button>
            <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={() => nextPage()}
                disabled = {events.length === 0}
            >
                Следующие
            </button>
          </div>
           <div className="mb-2">
            C {pageCount*pageLimit + 1} по {(pageCount + 1)*pageLimit}
           </div>
        </div>
      </div>

      {/* Таблица событий */}
      <div className="card shadow-sm">
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-sm table-hover mb-0 align-middle">
              <thead className="table-primary">
                <tr>
                  {projectId === null && <th>Проект</th>}
                  {instituteId === null && <th>Учреждение</th>}
                  {programId === null && <th>Программа</th>}
                  <th>Продукт</th>
                  <th>Тип</th>
                  <th>Статус</th>
                  <th>Начало</th>
                  <th>Завершение</th>
                </tr>
              </thead>
              <tbody>
                {events.length === 0 && (
                  <tr>
                    <td
                      colSpan={
                        (instituteId === null ? 1 : 0) +
                        (programId === null ? 1 : 0) +
                        5
                      }
                      className="text-center py-3 text-muted"
                    >
                      Нет событий по выбранным критериям
                    </td>
                  </tr>
                )}
                {events.map(renderEventRow)}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}



// WEBPACK FOOTER //
// ./src/ui/workspace-screens/event/events-screen.js